// ============================================================
//  MainLayout.tsx — Enterprise Shell (SAP-style)
//  ┌─────────────────────────────────────────────┐
//  │ Topbar (fixed)                              │  48px
//  ├────────┬────────────────────────────────────┤
//  │        │ Breadcrumb (sticky)                │  34px
//  │Sidebar │────────────────────────────────────│
//  │(fixed) │ Main content (scrolls INTERNALLY)  │  flex
//  │        │                                    │
//  ├────────┴────────────────────────────────────┤
//  │ TabBar (fixed) — SAP-style open pages       │  38px
//  └─────────────────────────────────────────────┘
// ============================================================
import React, { useEffect, useState, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import Sidebar       from "./Sidebar";
import Topbar        from "./Topbar";
import BreadcrumbBar from "./BreadcrumbBar";
import TabBar        from "./TabBar";
import { OpenTabsProvider, useOpenTabs } from "./OpenTabsContext";
import PersistentPageStack from "./PersistentPageStack";
import { COLORS, DESIGN, cssVarsStyle } from "../theme/colors";

export const SidebarCtx = createContext<{
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
}>({ collapsed: false, setCollapsed: () => {} });

export const useSidebar = () => useContext(SidebarCtx);

const SIDEBAR_FULL = DESIGN.shell.sidebarFull;
const SIDEBAR_MINI = DESIGN.shell.sidebarMini;
const TOPBAR_H     = DESIGN.shell.topbarHeight;
const TABBAR_H     = DESIGN.shell.tabbarHeight;

const CachedPageArea = ({ activePath, children }: { activePath: string; children: React.ReactNode }) => {
  const { tabs } = useOpenTabs();
  return (
    <PersistentPageStack activePath={activePath} openPaths={tabs.map(tab => tab.path)}>
      {children}
    </PersistentPageStack>
  );
};

const MainLayout = ({ children }: any) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobileShell, setIsMobileShell] = useState(false);
  const location = useLocation();
  const w = collapsed ? SIDEBAR_MINI : SIDEBAR_FULL;
  const mobileSidebarWidth = "min(320px, 84vw)";

  useEffect(() => {
    const updateShellMode = () => setIsMobileShell(window.innerWidth <= 900);
    updateShellMode();
    window.addEventListener("resize", updateShellMode);
    return () => window.removeEventListener("resize", updateShellMode);
  }, []);

  useEffect(() => {
    if (isMobileShell) setCollapsed(true);
  }, [isMobileShell, location.pathname]);

  return (
    <OpenTabsProvider>
      <SidebarCtx.Provider value={{ collapsed, setCollapsed }}>
        {/* Global styles for polished look */}
        <style>{`
          html, body, #root {
            height: 100vh;
            overflow: hidden;
            margin: 0;
          }
          @keyframes fadeSlideIn {
            from { opacity: 0; transform: translateY(6px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .erp-main-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
          .erp-main-scroll::-webkit-scrollbar-track { background: var(--erp-divider); }
          .erp-main-scroll::-webkit-scrollbar-thumb {
            background: var(--erp-primary);
            border-radius: 4px;
            border: 2px solid var(--erp-divider);
          }
          .erp-main-scroll::-webkit-scrollbar-thumb:hover {
            background: var(--erp-secondary);
          }
          .erp-page-enter {
            animation: fadeSlideIn 0.28s ease-out;
          }
          /* Subtle grid background pattern */
          .erp-content-bg {
            background: var(--erp-page-bg);
          }
          .erp-mobile-menu-button {
            position: fixed;
            top: ${Math.max(8, Math.floor(TOPBAR_H / 2) - 15)}px;
            left: 64px;
            z-index: 1500;
            width: 36px;
            height: 32px;
            border: 1px solid rgba(148, 163, 184, 0.28);
            border-radius: 6px;
            background: rgba(15, 23, 42, 0.92);
            box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
            display: none;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            gap: 4px;
            cursor: pointer;
          }
          .erp-mobile-menu-button span {
            width: 16px;
            height: 2px;
            border-radius: 2px;
            background: #CBD5E1;
          }
          @media (max-width: 900px) {
            .erp-mobile-menu-button {
              display: flex;
            }
          }
        `}</style>

        <div className="erp-unified-ui erp-shell" style={{
          ...cssVarsStyle,
          display: "flex", flexDirection: "column",
          height: "100vh", width: "100vw",
          background: "#0F172A",
          fontFamily: "var(--erp-font)",
          overflow: "hidden",
        }}>

          {/* ── Topbar ─────────────────────────────────────── */}
          <div style={{ height: TOPBAR_H, flexShrink: 0, zIndex: 1300 }}>
            <Topbar />
          </div>

          {isMobileShell && collapsed && (
            <button
              className="erp-mobile-menu-button app-no-drag"
              onClick={() => setCollapsed(false)}
              title="Open Menu"
              aria-label="Open Menu"
            >
              <span />
              <span />
              <span />
            </button>
          )}

          {/* ── Middle row: Sidebar + Content ─────────────── */}
          <div style={{
            flex: 1, minHeight: 0,
            display: "flex", flexDirection: "row",
            overflow: "hidden",
          }}>

            {/* Sidebar */}
            <div style={{
              width: isMobileShell ? mobileSidebarWidth : w,
              minWidth: isMobileShell ? 0 : w,
              flexShrink: 0,
              height: isMobileShell ? `calc(100vh - ${TOPBAR_H + TABBAR_H}px)` : "100%",
              transition: isMobileShell
                ? "transform 0.22s cubic-bezier(.4,0,.2,1)"
                : "width 0.25s cubic-bezier(.4,0,.2,1), min-width 0.25s cubic-bezier(.4,0,.2,1)",
              boxShadow: "2px 0 12px rgba(0,0,0,0.25)",
              position: isMobileShell ? "fixed" : "relative",
              left: isMobileShell ? 0 : undefined,
              top: isMobileShell ? TOPBAR_H : undefined,
              bottom: isMobileShell ? TABBAR_H : undefined,
              transform: isMobileShell && collapsed ? "translateX(-105%)" : "translateX(0)",
              zIndex: isMobileShell ? 1450 : 1200,
            }}>
              <Sidebar />
            </div>

            {isMobileShell && !collapsed && (
              <button
                aria-label="Close Menu"
                onClick={() => setCollapsed(true)}
                style={{
                  position: "fixed",
                  inset: `${TOPBAR_H}px 0 ${TABBAR_H}px 0`,
                  zIndex: 1400,
                  border: 0,
                  background: "rgba(15,23,42,0.48)",
                  backdropFilter: "blur(3px)",
                  cursor: "pointer",
                }}
              />
            )}

            {/* Main content area */}
            <div style={{
              flex: 1, minWidth: 0,
              display: "flex", flexDirection: "column",
              overflow: "hidden",
            }}>
              {/* Breadcrumb (sticky at top of content) */}
              <div style={{ flexShrink: 0 }}>
                <BreadcrumbBar />
              </div>

              {/* Actual scrollable content */}
              <main
                className="erp-main-scroll erp-content-bg"
                style={{
                  flex: 1, minHeight: 0,
                  overflowY: "auto", overflowX: "hidden",
                  paddingBottom: 4,
                  background: COLORS.pageBg,
                }}
              >
                <div className="erp-page-enter" style={{ minHeight: "100%" }}>
                  <CachedPageArea activePath={location.pathname}>
                    {children}
                  </CachedPageArea>
                </div>
              </main>
            </div>
          </div>

          {/* ── TabBar at bottom (SAP-style) ──────────────── */}
          <div style={{ height: TABBAR_H, flexShrink: 0 }}>
            <TabBar />
          </div>

        </div>
      </SidebarCtx.Provider>
    </OpenTabsProvider>
  );
};

export default MainLayout;
